import type { Field } from '../../../types';
import { uniqueEntries, uniqueMultivaluedEntries } from './uniqueEntries';
import { Column, ColumnType, MatchColumnsProps, MatchedOptions } from '../types';
import { hasMappedValue } from './getFieldConfiguration';

export const setColumn = <T extends string>(
  oldColumn: Column<T>,
  field?: Field<T>,
  data?: MatchColumnsProps<T>['data'],
  autoMapSelectValues?: boolean
): Column<T> => {
  switch (field?.fieldType.type) {
    case 'select':
      const fieldOptions = field.fieldType.options;
      const uniqueData = uniqueEntries(data || [], oldColumn.index) as MatchedOptions<T>[];
      const matchedOptions = autoMapSelectValues
        ? uniqueData.map((record) => {
            const value = fieldOptions.find(
              (fieldOption) =>
                hasMappedValue(fieldOption.value, record.entry, field) ||
                hasMappedValue(fieldOption.label, record.entry, field)
            )?.value;
            return value ? ({ ...record, value } as MatchedOptions<T>) : (record as MatchedOptions<T>);
          })
        : uniqueData;
      const allMatched = matchedOptions.filter((o) => o.value).length === uniqueData?.length;

      return {
        ...oldColumn,
        type: allMatched ? ColumnType.matchedSelectOptions : ColumnType.matchedSelect,
        value: field.key,
        matchedOptions
      };
    case 'multiselect':
      const multivaluedFieldOptions = field.fieldType.options;
      const multivaluedUniqueData = uniqueMultivaluedEntries(
        data || [],
        oldColumn.index,
        field.fieldType.valueSeparator
      ) as MatchedOptions<T>[];
      const multivaluedMatchedOptions = autoMapSelectValues
        ? multivaluedUniqueData.map((record) => {
            const value = multivaluedFieldOptions.find((fieldOption) => {
              return (
                hasMappedValue(fieldOption.value, record.entry, field) ||
                hasMappedValue(fieldOption.label, record.entry, field)
              );
            })?.value;
            return value ? ({ ...record, value } as MatchedOptions<T>) : (record as MatchedOptions<T>);
          })
        : multivaluedUniqueData;
      const multivaluedAllMatched =
        multivaluedMatchedOptions.filter((o) => o.value).length === multivaluedUniqueData?.length;

      return {
        ...oldColumn,
        type: multivaluedAllMatched ? ColumnType.matchedSelectOptions : ColumnType.matchedSelect,
        value: field.key,
        matchedOptions: multivaluedMatchedOptions
      };
    case 'checkbox':
      return { index: oldColumn.index, type: ColumnType.matchedCheckbox, value: field.key, header: oldColumn.header };
    case 'input':
      return { index: oldColumn.index, type: ColumnType.matched, value: field.key, header: oldColumn.header };
    default:
      return { index: oldColumn.index, header: oldColumn.header, type: ColumnType.empty };
  }
};
